import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  getDocs,
  Timestamp,
  serverTimestamp,
  writeBatch,
  limit,
} from 'firebase/firestore';
import { db } from '@/backend/lib/firebase';
import { auth } from '@/backend/lib/firebase';
import {
  EnergyConsumption,
  CreateEnergyConsumptionInput,
  UpdateEnergyConsumptionInput,
  ConsumptionSummary,
  StudentConsumptionSummary,
} from '@/shared/types/energy';
import { findAllContractsForRoom } from './contracts';

const COLLECTION_NAME = 'energy_consumption';

// ===== Helper Functions =====

export function formatBillingPeriodKey(month: number, year: number): string {
  return `${year}-${month.toString().padStart(2, '0')}`;
}

export function parseBillingPeriodKey(key: string): { month: number; year: number } {
  const [year, month] = key.split('-').map(Number);
  return { month, year };
}

export function formatBillingPeriodDisplay(month: number, year: number): string {
  const date = new Date(year, month - 1, 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

export function calculateSummary(records: EnergyConsumption[]): ConsumptionSummary {
  return {
    totalRecords: records.length,
    exceededCount: records.filter(r => r.exceedsLimit).length,
    pendingNotifications: records.filter(
      r => r.exceedsLimit && !r.notificationSent && r.studentId
    ).length,
  };
}

/**
 * Calculate summary statistics for student consumption view
 */
export function calculateStudentConsumptionSummary(
  records: EnergyConsumption[]
): StudentConsumptionSummary {
  if (records.length === 0) {
    return { totalRecords: 0, totalKwh: 0, averageKwh: 0, exceededCount: 0, currentLimit: null };
  }

  const totalKwh = records.reduce((sum, record) => sum + record.consumptionKwh, 0);
  const averageKwh = Math.round((totalKwh / records.length) * 100) / 100;
  const exceededCount = records.filter(r => r.exceedsLimit).length;
  const currentLimit = records[0]?.contractMonthlyLimit || null;

  return { totalRecords: records.length, totalKwh, averageKwh, exceededCount, currentLimit };
}

function generateConsumptionId(
  residenceId: string,
  roomTypeId: string,
  billingPeriodKey: string,
  studentId?: string
): string {
  const base = `${residenceId}_${roomTypeId}_${billingPeriodKey}`;
  return studentId ? `${base}_${studentId}` : base;
}

// ===== CRUD Operations =====

export async function createEnergyConsumption(
  data: CreateEnergyConsumptionInput
): Promise<EnergyConsumption[]> {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');

  const billingPeriodKey = formatBillingPeriodKey(data.billingMonth, data.billingYear);

  // Check for duplicate
  const exists = await consumptionExists(data.residenceId, data.roomTypeId, billingPeriodKey);
  if (exists) {
    throw new Error('Consumption already recorded for this room and period');
  }

  // Find all active students in this room for the billing period
  const contracts = await findAllContractsForRoom(
    data.residenceId,
    data.roomTypeId,
    data.billingMonth,
    data.billingYear
  );

  const now = Timestamp.now();

  // No active contracts — create a single no-contract record
  if (contracts.length === 0) {
    const id = generateConsumptionId(data.residenceId, data.roomTypeId, billingPeriodKey);
    const record: EnergyConsumption = {
      id,
      residenceId: data.residenceId,
      residenceName: data.residenceName,
      roomTypeId: data.roomTypeId,
      roomTypeName: data.roomTypeName,
      billingPeriod: { month: data.billingMonth, year: data.billingYear },
      billingPeriodKey,
      consumptionKwh: data.consumptionKwh,
      contractId: null,
      studentId: null,
      studentName: null,
      studentEmail: null,
      contractMonthlyLimit: null,
      exceedsLimit: false,
      excessKwh: null,
      excessAmountEur: null,
      paymentStatus: null,
      paidAt: null,
      linkedRecordIds: [],
      notificationSent: false,
      notificationSentAt: null,
      createdAt: now,
      createdBy: user.uid,
      updatedAt: now,
      updatedBy: user.uid,
    };
    await setDoc(doc(db, COLLECTION_NAME, id), record);
    return [record];
  }

  // Determine all IDs upfront so we can cross-reference linkedRecordIds
  const allIds = contracts.map(c =>
    generateConsumptionId(data.residenceId, data.roomTypeId, billingPeriodKey, c.studentId)
  );

  const records: EnergyConsumption[] = contracts.map((contract, i) => {
    const exceedsLimit =
      contract.monthlyKwhLimit != null && data.consumptionKwh > contract.monthlyKwhLimit;
    const excessKwh = exceedsLimit ? data.consumptionKwh - contract.monthlyKwhLimit : null;
    const excessAmountEur =
      excessKwh != null && data.pricePerExcessKwh != null
        ? Math.round(excessKwh * data.pricePerExcessKwh * 100) / 100
        : null;

    return {
      id: allIds[i],
      residenceId: data.residenceId,
      residenceName: data.residenceName,
      roomTypeId: data.roomTypeId,
      roomTypeName: data.roomTypeName,
      billingPeriod: { month: data.billingMonth, year: data.billingYear },
      billingPeriodKey,
      consumptionKwh: data.consumptionKwh,
      contractId: contract.contractId,
      studentId: contract.studentId,
      studentName: contract.studentName,
      studentEmail: contract.studentEmail,
      contractMonthlyLimit: contract.monthlyKwhLimit,
      exceedsLimit,
      excessKwh,
      excessAmountEur,
      paymentStatus: exceedsLimit ? 'unpaid' : null,
      paidAt: null,
      linkedRecordIds: allIds.filter((_, j) => j !== i),
      notificationSent: false,
      notificationSentAt: null,
      createdAt: now,
      createdBy: user.uid,
      updatedAt: now,
      updatedBy: user.uid,
    };
  });

  const batch = writeBatch(db);
  for (const record of records) {
    batch.set(doc(db, COLLECTION_NAME, record.id), record);
  }
  await batch.commit();

  return records;
}

export async function updateEnergyConsumption(
  id: string,
  data: UpdateEnergyConsumptionInput
): Promise<EnergyConsumption> {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');

  const primary = await getEnergyConsumption(id);
  if (!primary) throw new Error('Consumption record not found');

  const linkedIds = primary.linkedRecordIds ?? [];

  // Fields that must stay in sync across all records for the same room+period
  const sharedFieldKeys: (keyof UpdateEnergyConsumptionInput)[] = [
    'consumptionKwh', 'exceedsLimit', 'excessKwh', 'excessAmountEur', 'paymentStatus', 'paidAt',
  ];
  const sharedData = Object.fromEntries(
    sharedFieldKeys.filter(k => k in data).map(k => [k, data[k as keyof typeof data]])
  );

  if (linkedIds.length > 0 && Object.keys(sharedData).length > 0) {
    const batch = writeBatch(db);
    batch.update(doc(db, COLLECTION_NAME, id), {
      ...data,
      updatedAt: serverTimestamp(),
      updatedBy: user.uid,
    });
    for (const linkedId of linkedIds) {
      batch.update(doc(db, COLLECTION_NAME, linkedId), {
        ...sharedData,
        updatedAt: serverTimestamp(),
        updatedBy: user.uid,
      });
    }
    await batch.commit();
  } else {
    await updateDoc(doc(db, COLLECTION_NAME, id), {
      ...data,
      updatedAt: serverTimestamp(),
      updatedBy: user.uid,
    });
  }

  const updated = await getEnergyConsumption(id);
  if (!updated) throw new Error('Failed to retrieve updated consumption');
  return updated;
}

export async function getEnergyConsumption(id: string): Promise<EnergyConsumption | null> {
  const docRef = doc(db, COLLECTION_NAME, id);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;
  return { id: docSnap.id, ...docSnap.data() } as EnergyConsumption;
}

export async function getConsumptionByResidence(
  residenceId: string,
  filters?: { year?: number; month?: number }
): Promise<EnergyConsumption[]> {
  const colRef = collection(db, COLLECTION_NAME);
  let q = query(
    colRef,
    where('residenceId', '==', residenceId),
    orderBy('billingPeriodKey', 'desc')
  );

  if (filters?.year && filters?.month) {
    const periodKey = formatBillingPeriodKey(filters.month, filters.year);
    q = query(
      colRef,
      where('residenceId', '==', residenceId),
      where('billingPeriodKey', '==', periodKey)
    );
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as EnergyConsumption));
}

export async function getConsumptionByStudent(
  studentId: string
): Promise<EnergyConsumption[]> {
  const colRef = collection(db, COLLECTION_NAME);
  const q = query(
    colRef,
    where('studentId', '==', studentId),
    orderBy('billingPeriodKey', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as EnergyConsumption));
}

export async function consumptionExists(
  residenceId: string,
  roomTypeId: string,
  billingPeriodKey: string
): Promise<boolean> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('residenceId', '==', residenceId),
    where('roomTypeId', '==', roomTypeId),
    where('billingPeriodKey', '==', billingPeriodKey),
    limit(1)
  );
  const snapshot = await getDocs(q);
  return !snapshot.empty;
}

export async function markNotificationSent(id: string): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, id);
  await updateDoc(docRef, {
    notificationSent: true,
    notificationSentAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    updatedBy: auth.currentUser?.uid || 'system',
  });
}

export async function markAsPaid(record: EnergyConsumption): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');

  const batch = writeBatch(db);
  const allIds = [record.id, ...(record.linkedRecordIds ?? [])];
  for (const id of allIds) {
    batch.update(doc(db, COLLECTION_NAME, id), {
      paymentStatus: 'paid',
      paidAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      updatedBy: user.uid,
    });
  }
  await batch.commit();
}

export async function markAsUnpaid(record: EnergyConsumption): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');

  const batch = writeBatch(db);
  const allIds = [record.id, ...(record.linkedRecordIds ?? [])];
  for (const id of allIds) {
    batch.update(doc(db, COLLECTION_NAME, id), {
      paymentStatus: 'unpaid',
      paidAt: null,
      updatedAt: serverTimestamp(),
      updatedBy: user.uid,
    });
  }
  await batch.commit();
}

export async function deleteEnergyConsumption(record: EnergyConsumption): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');

  const allIds = [record.id, ...(record.linkedRecordIds ?? [])];

  if (allIds.length === 1) {
    await deleteDoc(doc(db, COLLECTION_NAME, record.id));
  } else {
    const batch = writeBatch(db);
    for (const id of allIds) {
      batch.delete(doc(db, COLLECTION_NAME, id));
    }
    await batch.commit();
  }
}
